import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { AlertTriangleIcon } from './icons.tsx';

/**
 * Replacement for `window.confirm`.
 *
 * The native dialog cannot be styled, announces the origin ("localhost:5173
 * says"), and blocks the whole renderer while it is open. This keeps the same
 * promise-shaped call site — `if (await confirm(...))` — so replacing a call is
 * mechanical, but the dialog belongs to the app.
 */

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the primary action red, for anything that destroys data. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Where focus was before the dialog took it, so it can be handed back.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        previousFocusRef.current = document.activeElement as HTMLElement | null;
        setPending({ options, resolve });
      }),
    [],
  );

  const settle = useCallback(
    (value: boolean) => {
      setPending((current) => {
        current?.resolve(value);
        return null;
      });

      previousFocusRef.current?.focus();
    },
    [],
  );

  useEffect(() => {
    if (!pending) return;

    confirmButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        settle(false);
        return;
      }

      // A two-button dialog is small enough that cycling between them by hand
      // is simpler — and more reliable — than a general focus trap.
      if (event.key === 'Tab') {
        const focusable = document.querySelectorAll<HTMLElement>('[data-confirm-focusable]');
        if (focusable.length === 0) return;

        const list = [...focusable];
        const index = list.indexOf(document.activeElement as HTMLElement);
        const next = event.shiftKey ? index - 1 : index + 1;

        event.preventDefault();
        list[(next + list.length) % list.length]?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pending, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      {pending ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
            aria-hidden="true"
            onClick={() => {
              settle(false);
            }}
          />

          <div className="relative w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex gap-3">
              {pending.options.destructive ? (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-400">
                  <AlertTriangleIcon className="h-5 w-5" />
                </span>
              ) : null}

              <div className="min-w-0">
                <h2
                  id="confirm-title"
                  className="text-sm font-semibold text-slate-900 dark:text-slate-50"
                >
                  {pending.options.title}
                </h2>

                {pending.options.message ? (
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {pending.options.message}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                data-confirm-focusable
                className="lg-btn-secondary"
                onClick={() => {
                  settle(false);
                }}
              >
                {pending.options.cancelLabel ?? 'Cancel'}
              </button>

              <button
                ref={confirmButtonRef}
                type="button"
                data-confirm-focusable
                className={clsx(pending.options.destructive ? 'lg-btn-danger' : 'lg-btn-primary')}
                onClick={() => {
                  settle(true);
                }}
              >
                {pending.options.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used inside ConfirmProvider');
  return context;
}
